import { useState } from "react";

import { partition, sortBy } from "lodash/fp";

import {
  Stack,
  Typography,
  Divider,
  Collapse,
  ButtonBase,
} from "@mui/material";

import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import useAuth from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { pluralize } from "@app/common/utils";
import {
  EnterpriseWorkspaceInvite,
  EnterpriseWorkspaceUser,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import WorkspaceSidePanelRow from "./WorkspaceSidePanelRow";
import WorkspaceActionButton from "../components/WorkspaceActionButton";
import AddMemberToWorkspaceModal from "../modals/AddMemberToWorkspaceModal";

const NUM_UNCOLLAPSED_ROWS = 5;

const sortUsers = (
  workspaceUsers: EnterpriseWorkspaceUser[],
  workspaceInvites: EnterpriseWorkspaceInvite[],
  currentUserId?: string
): (EnterpriseWorkspaceUser | EnterpriseWorkspaceInvite)[] => {
  const [currentUser, otherUsers] = partition(
    (user) => `${user.userId}` === `${currentUserId}`,
    workspaceUsers
  );

  const sortedInvites = sortBy("email", workspaceInvites).filter(
    (invite) => invite.status === "pending"
  );
  return [...currentUser, ...sortBy("fullName", otherUsers), ...sortedInvites];
};

const WorkspaceSidePanel = () => {
  const { user } = useAuth();
  const { workspace, workspaceIsInactive } = useWorkspace();

  const [showAll, setShowAll] = useState(false);
  const [addToWorkspaceOpen, setAddToWorkspaceOpen] = useState(false);
  const { canInviteUsers } = useFeatureAccess();
  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  const workspaceMembers = sortUsers(
    workspace.users,
    workspace.invites,
    user?.id
  );

  const numCollapsedRows = workspaceMembers.length - NUM_UNCOLLAPSED_ROWS;

  return (
    <>
      <AddMemberToWorkspaceModal
        open={addToWorkspaceOpen}
        onClose={() => setAddToWorkspaceOpen(false)}
        workspaceName={workspace.name}
      />
      <Stack
        alignItems="start"
        spacing={2}
        sx={{
          border: "solid 1px",
          height: "100%",
          borderRadius: "8px",
          borderColor: "shades.neutral.100",
          width: "100%",
          color: "shades.neutral.800",
          p: 2,
        }}
      >
        <Typography variant="h2">{workspace.name}</Typography>
        <Divider sx={{ width: "100%" }} />
        <Typography variant="bodySecondaryMed">
          Members ({workspaceMembers.length})
        </Typography>
        <Stack spacing={2} sx={{ width: "100%", overflow: "auto" }}>
          {/* Initial 5 members */}
          {workspaceMembers
            .slice(0, NUM_UNCOLLAPSED_ROWS)
            .map((userOrInvite) => {
              const isUser = userOrInvite.type === "user";
              return (
                <WorkspaceSidePanelRow
                  key={userOrInvite.email}
                  userOrInvite={userOrInvite}
                  isCurrentUser={
                    isUser && `${userOrInvite.userId}` === `${user?.id}`
                  }
                />
              );
            })}
          {/* 'Hidden' Members */}
          <Collapse in={showAll} unmountOnExit>
            <Stack spacing={2} sx={{ flexGrow: 1 }}>
              {workspaceMembers
                .slice(NUM_UNCOLLAPSED_ROWS)
                .map((userOrInvite) => (
                  <WorkspaceSidePanelRow
                    key={userOrInvite.email}
                    userOrInvite={userOrInvite}
                  />
                ))}
            </Stack>
          </Collapse>
        </Stack>
        {numCollapsedRows > 0 && (
          <ButtonBase
            sx={{
              color: "shades.neutral.500",
              my: 2,
              borderRadius: "8px",
            }}
            onClick={toggleShowAll}
          >
            <Typography variant="bodySecondaryReg">
              {showAll ? "–" : "+"} {numCollapsedRows}{" "}
              {`${pluralize("other", numCollapsedRows)}`}
            </Typography>
          </ButtonBase>
        )}
        {canInviteUsers && (
          <UpsellTooltipWrapper show={workspaceIsInactive}>
            <WorkspaceActionButton
              disabled={workspaceIsInactive}
              text="Add members"
              onClick={() => setAddToWorkspaceOpen(true)}
            />
          </UpsellTooltipWrapper>
        )}
      </Stack>
    </>
  );
};

export default WorkspaceSidePanel;
